import React, { useState } from 'react';
import { Plane, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const FlightInfoCard = ({ onComplete, initialFlightNumber = '', locationType = 'departure' }: any) => {
  const { t } = useTranslation();
  const [flightNumber, setFlightNumber] = useState(initialFlightNumber);
  const [hasFlightInfo, setHasFlightInfo] = useState(false);

  const handleContinue = () => {
    onComplete(flightNumber.trim() || null);
  };

  const handleNoFlight = () => {
    onComplete(null);
  };

  const isArrival = locationType === 'arrival';
  const isDeparture = locationType === 'departure';
  const isBoth = locationType === 'both';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4"
    >
      <div className="relative group">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-sky-600 to-indigo-600 rounded-2xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
        
        <div className="relative bg-background dark:bg-slate-800 rounded-2xl p-4 border border-border dark:border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center">
                <Plane size={20} className="text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground dark:text-slate-200">Flyinformasjon</h4>
                <p className="text-xs text-muted-foreground">
                  {isBoth ? t('auto.bde_avgang_og_ankomst', 'Both departure and arrival') : isArrival ? 'Arrival from airport' : 'Departure to airport'}
                </p>
              </div>
            </div>
            {flightNumber.trim() && (
              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check size={16} className="text-green-600 dark:text-green-400" />
              </div>
            )}
          </div>

          {/* Flight number input */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-foreground dark:text-slate-300 mb-2">
                ✈️ Flynummer (valgfritt)
              </label>
              <input
                type="text"
                value={flightNumber}
                onChange={(e: any) => setFlightNumber(e.target.value.toUpperCase())}
                placeholder={t('flight.searchFlightExample', 'f.eks. SK4024, DY621')}
                className="w-full px-4 py-3 bg-muted/50 dark:bg-slate-900/50 border border-border dark:border-slate-700 rounded-xl text-foreground dark:text-slate-200 placeholder-slate-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
              />
              <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-2">
                📝 Add flight number for accurate timing
              </p>
            </div>

            {flightNumber.trim() && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="p-3 bg-sky-50 dark:bg-sky-900/20 rounded-xl"
              >
                <p className="text-sm text-sky-800 dark:text-sky-300">
                  ✓ Flynummer: <strong>{flightNumber}</strong>
                </p>
              </motion.div>
            )}
          </div>

          {/* Action buttons */}
          <div className="mt-4 space-y-2">
            <button
              onClick={handleContinue}
              className="w-full py-3 px-4 bg-gradient-to-r from-sky-500 to-indigo-500 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-sky-500/50 transition-all duration-300"
            >
              {flightNumber.trim() ? 'Fortsett med flynummer' : 'Fortsett uten flynummer'}
            </button>
            
            {!flightNumber.trim() && (
              <button
                onClick={handleNoFlight}
                className="w-full py-2 px-4 text-muted-foreground dark:text-muted-foreground hover:text-foreground dark:hover:text-slate-200 text-sm transition-colors"
              >
                I don't have a flight number now
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FlightInfoCard;
